import { useMutation } from '@apollo/client';
import { Grid, Icon, Paper, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { Module } from '@thro-types/modules-selections';

import { useCallback, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import DISCHARGE_STUDENT_MUTATION from 'gql/mutations/ModuleSelectionsService/dischargeStudent.gql';
import QUERY_LOTTERY_RESULT from 'gql/queries/ModuleSelectionsService/getLatestCalculatedLotteryResultForStudent.gql';

import { useModulesQuery } from 'api/hooks/modules';
import { TypedGridColumns } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { DataGrid } from 'app/components/DataGrid';
import { SelectionState } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import {
  getCurrentSelectionState,
  userDataToLecturerOption
} from 'app/containers/AWPM-FWPM/helper';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import {
  LatestCalculatedLotteryResultsForStudent,
  LotteryResult,
  ModuleWithNumberOfActiveParticipants
} from 'types/AppTypes';

import { ModuleStudentSelection } from '../../ModuleSelection';

interface ReceivedModulesProps {
  moduleSelection: ModuleStudentSelection;
  latestLottery: LatestCalculatedLotteryResultsForStudent;
}

export const ReceivedModules = ({ moduleSelection, latestLottery }: ReceivedModulesProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [dischargeStudent] = useMutation(DISCHARGE_STUDENT_MUTATION, {
    context: { clientName: 'federation' }
  });

  const { data: modules, isLoading } = useModulesQuery({
    params: { selectionId: moduleSelection.id }
  });

  const [currentlyEditingModule, setCurrentlyEditingModule] = useState<any>();
  const [signOutFromCourseDialogOpen, setSignOutFromCourseDialogOpen] = useState(false);

  const getRows = (latestLottery: LotteryResult[], modules: Module[]) => {
    let isAssignedToModule = false;
    const rows: Array<unknown> = [];
    latestLottery?.forEach(result => {
      if (result?.assignedToModule && !result.modulePreference?.studentDischarge) {
        isAssignedToModule = true;
        const studentModule = modules.find(item => item.id === result?.modulePreference?.moduleId);
        rows.push({
          ...studentModule,
          modulePreferenceId: result.modulePreference?.id
        });
      }
    });
    return isAssignedToModule ? rows : [];
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-trash-alt"
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.pages.studentView.signOut())}
          onClick={() => {
            setCurrentlyEditingModule(params.row);
            setSignOutFromCourseDialogOpen(true);
          }}
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<
    ModuleWithNumberOfActiveParticipants & { moduleOfficerUser: string }
  > => {
    return [
      {
        flex: 3,
        minWidth: isMobile ? 50 : 200,
        headerName: t(translations.pages.modules.create.modulePreview.title()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: isMobile ? 80 : 200,
        headerName: t(translations.pages.modules.lecturer()),
        field: 'moduleOfficerUser',
        renderCell: params => (
          <Tooltip title={userDataToLecturerOption(params.row.moduleOfficerUser, t).label}>
            <span>{userDataToLecturerOption(params.row.moduleOfficerUser, t).label}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: isMobile ? 60 : 200,
        headerName: t(translations.pages.studentView.numberOfParticipants()),
        field: 'numberOfActiveParticipants',
        renderCell: params => (
          <Tooltip title={params.row.numberOfActiveParticipants}>
            <span>{params.row.numberOfActiveParticipants}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: isMobile ? 80 : 200,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  const columnsReadOnly = useMemo((): TypedGridColumns<
    ModuleWithNumberOfActiveParticipants & { moduleOfficerUser: string }
  > => {
    return [
      {
        flex: 3,
        minWidth: 200,
        headerName: t(translations.pages.modules.create.modulePreview.title()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.lecturer()),
        field: 'moduleOfficerUser',
        renderCell: params => (
          <Tooltip title={userDataToLecturerOption(params.row.moduleOfficerUser, t).label}>
            <span>{userDataToLecturerOption(params.row.moduleOfficerUser, t).label}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.studentView.numberOfParticipants()),
        field: 'numberOfActiveParticipants',
        renderCell: params => (
          <Tooltip title={params.row.numberOfActiveParticipants}>
            <span>{params.row.numberOfActiveParticipants}</span>
          </Tooltip>
        )
      }
    ];
  }, [t]);

  const handleSignOutFromCourse = async () => {
    const { errors } = await dischargeStudent({
      variables: {
        modulePreferenceId: parseInt(currentlyEditingModule?.modulePreferenceId, 10)
      },
      refetchQueries: [QUERY_LOTTERY_RESULT]
    });

    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.dischargeStudentFailed())));
    } else {
      dispatch(
        AlertActions.success(t(translations.pages.modules.alerts.dischargeStudentSuccessful()))
      );
    }
  };

  return (
    <>
      <ConfirmationDialog
        open={signOutFromCourseDialogOpen}
        title={t(translations.pages.studentView.signOutDialogTitle())}
        onClose={() => {
          setSignOutFromCourseDialogOpen(false);
        }}
        onConfirm={() => {
          setSignOutFromCourseDialogOpen(false);
          handleSignOutFromCourse();
        }}
      >
        <div>{t(translations.pages.studentView.signOutDialog())}</div>
      </ConfirmationDialog>
      <Grid
        item
        xs={12}
        container
        direction="column"
        flexGrow={1}
        component={Paper}
        style={{ height: isMobile ? '60vh' : '40vh', width: '100vw' }}
      >
        <DataGrid
          style={{ height: '100%' }}
          disableColumnFilter
          disableRowSelectionOnClick
          columns={
            getCurrentSelectionState(moduleSelection) !== SelectionState.ReadOnly
              ? columns
              : columnsReadOnly
          }
          rows={
            (getCurrentSelectionState(moduleSelection) === SelectionState.SignOutPossible ||
              getCurrentSelectionState(moduleSelection) === SelectionState.ReadOnly) &&
            !isLoading
              ? getRows(latestLottery.results, modules!)
              : []
          }
          paginationMode={'client'}
          checkboxSelection={false}
          columnVisibilityModel={{ numberOfActiveParticipants: !isMobile }}
          loading={isLoading}
        />
      </Grid>
    </>
  );
};
